import { useEffect, useState } from 'react';
import { PageCard } from '../../../Compounds/CardWithTitle';
import { AccountSelectionForm } from '../../../Compounds/UserCreation';
import { Autocomplete, TextField } from '@mui/material';
import { DEFAULT_ADVANCE_BUTTON_STYLE } from '../../../../utils/styleHelpers';
import { Account, InventoryAsset } from '../../../../types';
import { getUser, GridPreferences, PrefKey } from '../../../../store/User';
import { UpstackDataGrid } from '../../../Compounds/UpstackDataGrid/UpstackDataGrid';
import {
  ColumnGroupSortOrder,
  CreateInternalDetailsColumn,
  DefaultInventoryGridColumnDefs,
  OutsideInventoryDefaults
} from '../../Inventory/inventoryGridColumnDefs';
import { fetchOutsideInventory, bulkUpdateSupportLevelApi } from '../../../../Api/OutsideInventory/outsideInventoryApi';
import { Drawer } from '../../../Atoms';
import { DetailsDisplay } from '../../../Compounds/DetailsDrawer/DetailsDisplay';
import { getTitle, subTitle } from '../../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { useOutsideInventorySchemasStore } from '../../../../store/OutsideInventorySchema';
import { useSnackbar } from '../../../../Context/SnackbarContext';

export const SupportTab = () => {
  const { accounts } = getUser();
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [entitlementStatus, setEntitlementStatus] = useState<string>('');
  const [inventoryAssets, setInventoryAssets] = useState<InventoryAsset[]>([]);
  const [inventoryLoading, setInventoryLoading] = useState<boolean>(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<InventoryAsset | null>(null);
  const { fetchOutsideInventorySchemas } = useOutsideInventorySchemasStore();
  const [selectedInventoryAssets, setSelectedInventoryAssets] = useState<InventoryAsset[]>([]);
  const { setSnack } = useSnackbar();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onRowSelectionModelChange = (rowSelectionModel: any, _details: any) => {
    const selectedAssets = inventoryAssets.filter((asset) => rowSelectionModel.includes(asset.id));
    setSelectedInventoryAssets(selectedAssets);
  };

  const openDrawer = (asset: InventoryAsset) => {
    setSelectedAsset(asset);
    setDrawerOpen(true);
  };

  const columns = [
    ...CreateInternalDetailsColumn(openDrawer),
    ...DefaultInventoryGridColumnDefs.filter((col) => col.field !== 'details'),
    ...OutsideInventoryDefaults
  ];

  const initialColumnVisibility = [
    'details',
    'address',
    'productDisplay',
    'supplier.name',
    'upstack_support_managed',
    'providerAccountNumber'
  ];

  useEffect(() => {
    // Needed for details drawer schema-based fields
    fetchOutsideInventorySchemas();
  }, []);

  useEffect(() => {
    // Fetch inventory assets for the selected account
    if (selectedAccount) {
      setInventoryLoading(true);
      fetchOutsideInventory(selectedAccount.id).then((fetchedAssets) => {
        setInventoryLoading(false);
        if (fetchedAssets.data) {
          fetchedAssets.data.forEach((asset) => {
            if (asset.productDisplay === undefined) {
              asset.productDisplay = asset.product?.nameAbbreviation || asset.product?.name || '';
            }
          });
          setInventoryAssets(fetchedAssets.data);
        } else {
          setInventoryAssets([]);
        }
      });
    } else {
      setInventoryAssets([]);
    }
  }, [selectedAccount]);

  const handleSave = async () => {
    if (!selectedAccount || !entitlementStatus || selectedInventoryAssets.length === 0) {
      return;
    }

    try {
      const assetIds = selectedInventoryAssets.map((asset) => asset.id);
      const { data, error } = await bulkUpdateSupportLevelApi(assetIds, entitlementStatus === 'Supported');

      if (error) {
        setSnack({
          message: error.message || 'Error updating support entitlements',
          type: 'error',
          open: true
        });
        return;
      }

      // Success - show toast and refresh inventory list
      setSnack({
        message: `Successfully updated ${selectedInventoryAssets.length} inventory item${selectedInventoryAssets.length > 1 ? 's' : ''} to ${entitlementStatus}`,
        type: 'success',
        open: true
      });

      // data is the updated assets
      if (data) {
        const updatedAssets = data as InventoryAsset[];
        updatedAssets.forEach((updatedAsset) => {
          if (updatedAsset.productDisplay === undefined) {
            updatedAsset.productDisplay = updatedAsset.product?.nameAbbreviation || updatedAsset.product?.name || '';
          }
        });
        setSelectedInventoryAssets([]);
        setInventoryAssets(
          inventoryAssets.map((asset) => {
            const updatedAsset = updatedAssets.find((ua) => ua.id === asset.id);
            return updatedAsset ? updatedAsset : asset;
          })
        );
      }
    } catch (error) {
      setSnack({
        message: 'An unexpected error occurred while updating support entitlements',
        type: 'error',
        open: true
      });
    }
  };

  const handleAccountChange = (account: Account | undefined) => {
    setSelectedInventoryAssets([]);
    setSelectedAccount(account || null);
  };

  const isDropdownEnabled = selectedAccount !== null;
  const isSaveEnabled = selectedAccount !== null && entitlementStatus !== '' && selectedInventoryAssets.length > 0;

  const entitlementOptions = ['Supported', 'Not Supported'];

  const handleEntitlementChange = (_e: React.SyntheticEvent<Element, Event>, value: string | null) => {
    setEntitlementStatus(value || '');
  };

  return (
    <>
      <PageCard
        title="Support Entitlement"
        dataCy="admin-support-entitlement-tab"
        enableCollapse={false}>
        <div className="flex items-center justify-between mb-4 pr-8">
          <div className="flex items-center gap-24">
            <div className="flex flex-col">
              <label
                htmlFor="user-account-selection-form"
                className="text-sm font-medium text-gray-700 mb-1">
                Account:
              </label>
              <AccountSelectionForm
                accounts={accounts}
                selectedAccount={selectedAccount}
                setSelectedAccount={handleAccountChange}
                showLabel={false}
                styles="w-64"
                inputStyles="h-[1.75rem]"
              />
            </div>
            <div className="flex flex-col">
              <label
                htmlFor="entitlement-status"
                className="text-sm font-medium text-gray-700 mb-1">
                Update Entitlement to:
              </label>
              <Autocomplete
                id="entitlement-status"
                value={entitlementStatus}
                disabled={!isDropdownEnabled}
                disableClearable
                className="w-48"
                onChange={handleEntitlementChange}
                onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                options={entitlementOptions}
                getOptionLabel={(option: string) => option}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    InputProps={{ ...params.InputProps, style: { fontSize: '.875rem' } }}
                    label=""
                    className="h-[1.75rem]"
                    data-cy="entitlement-dropdown"
                    placeholder="Select status..."
                  />
                )}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    key={option}
                    className="text-sm14 py-1 pl-4 cursor-pointer">
                    {option}
                  </li>
                )}
              />
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={!isSaveEnabled}
            className={`${DEFAULT_ADVANCE_BUTTON_STYLE} mt-[1.75rem] ${!isSaveEnabled ? 'cursor-not-allowed' : ''}`}
            data-cy="save-entitlement-btn">
            Save {selectedInventoryAssets.length > 0 ? `(${selectedInventoryAssets.length})` : ''}
          </button>
        </div>
      </PageCard>
      {selectedAccount && !inventoryLoading && (
        <>
          <PageCard
            title={`Inventory (${inventoryAssets.length} items)`}
            childrenWrapperClass="min-h-80 max-w-[60vw] min-w-full"
            dataCy="admin-support-inventory-tab">
            <UpstackDataGrid
              dataCy="inventory-data-grid"
              columns={columns}
              defaultVisibleColumns={initialColumnVisibility}
              gridSettings={{} as GridPreferences}
              loadingData={inventoryLoading}
              page={'admin_support_entitlements' as PrefKey}
              rows={inventoryAssets}
              showSearch
              title={'All'}
              columnGroupSortOrder={ColumnGroupSortOrder}
              enableRowSelection={true}
              onRowSelectionModelChange={onRowSelectionModelChange}
            />
          </PageCard>
          <div>Drawer State: {drawerOpen ? 'Open' : 'Closed'}</div>
          {selectedAsset && (
            <Drawer
              data-testid="details-drawer"
              show={drawerOpen}
              title={getTitle('Inventory Details', selectedAsset, undefined)}
              subtitle={subTitle('inventory', selectedAsset, 'address')}
              setShow={() => setDrawerOpen(false)}
              showButtons={false}
              componentName={'inventory'}
              data={selectedAsset}>
              <DetailsDisplay
                data={selectedAsset}
                componentName={'inventory'}
                setShow={() => setDrawerOpen(false)}
              />
            </Drawer>
          )}
        </>
      )}
    </>
  );
};
